import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '@common/components';
import { fetchInventoryCategories } from '@api/dataSource';
import {
  INVENTORY_ROW_TYPES,
  INVENTORY_CATEGORY_HANDLING,
  INVENTORY_LAYOUTS_HOSTS,
  INVENTORY_LAYOUTS_ITEMS,
  INVENTORY_SCAN_BASIS,
  DATA_SOURCE_TYPES,
} from './constants';
import * as styles from './Step3InventoryConfig.module.css';
import * as wizardStyles from './styles.module.css';

/**
 * Get icon SVG for layout option
 */
function getLayoutIcon(layoutValue) {
  switch (layoutValue) {
    case 'long_form':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="3" rx="1" fill="currentColor" opacity="0.3"/>
          <rect x="3" y="9" width="18" height="2" rx="0.5" fill="currentColor"/>
          <rect x="3" y="13" width="18" height="2" rx="0.5" fill="currentColor"/>
          <rect x="3" y="17" width="18" height="2" rx="0.5" fill="currentColor"/>
        </svg>
      );
    case 'split_by_category':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="8" height="8" rx="1" fill="currentColor" opacity="0.5"/>
          <rect x="13" y="3" width="8" height="8" rx="1" fill="currentColor" opacity="0.5"/>
          <rect x="3" y="13" width="8" height="8" rx="1" fill="currentColor" opacity="0.5"/>
          <rect x="13" y="13" width="8" height="8" rx="1" fill="currentColor" opacity="0.5"/>
        </svg>
      );
    case 'pivot':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="4" height="18" rx="1" fill="currentColor" opacity="0.3"/>
          <rect x="9" y="3" width="12" height="4" rx="1" fill="currentColor" opacity="0.3"/>
          <rect x="9" y="9" width="4" height="4" fill="currentColor" opacity="0.6"/>
          <rect x="14" y="9" width="4" height="4" fill="currentColor" opacity="0.6"/>
          <rect x="9" y="14" width="4" height="4" fill="currentColor" opacity="0.6"/>
          <rect x="14" y="14" width="4" height="4" fill="currentColor" opacity="0.6"/>
        </svg>
      );
    case 'aggregated':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="3" rx="1" fill="currentColor" opacity="0.3"/>
          <rect x="3" y="9" width="12" height="2" rx="0.5" fill="currentColor"/>
          <rect x="16" y="9" width="5" height="2" rx="0.5" fill="currentColor" opacity="0.5"/>
          <rect x="3" y="13" width="10" height="2" rx="0.5" fill="currentColor"/>
          <rect x="14" y="13" width="7" height="2" rx="0.5" fill="currentColor" opacity="0.5"/>
          <rect x="3" y="17" width="14" height="2" rx="0.5" fill="currentColor"/>
          <rect x="18" y="17" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.5"/>
        </svg>
      );
    case 'expanded':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="3" rx="1" fill="currentColor" opacity="0.3"/>
          <rect x="3" y="9" width="18" height="2" rx="0.5" fill="currentColor"/>
          <rect x="5" y="12" width="16" height="1.5" rx="0.5" fill="currentColor" opacity="0.6"/>
          <rect x="5" y="14.5" width="16" height="1.5" rx="0.5" fill="currentColor" opacity="0.6"/>
          <rect x="3" y="17" width="18" height="2" rx="0.5" fill="currentColor"/>
        </svg>
      );
    default:
      return null;
  }
}

/**
 * Step 3 configuration for Inventory-based table widgets.
 * Allows configuration of:
 * - Row Type (Hosts or Inventory Items)
 * - Category Handling
 * - Table Layout (depends on Row Type)
 * - Scan Basis
 *
 * Config is stored inside dataSources[].config for inventory type
 */
function Step3InventoryConfig({ widgetData, onDataSourceConfigChange, validationErrors }) {
  const { t } = useTranslation();
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const { config } = widgetData;

  // Get inventory dataSource and its config
  const inventorySource = useMemo(
    () => (config.dataSources || []).find((ds) => ds.type === DATA_SOURCE_TYPES.INVENTORY),
    [config.dataSources],
  );
  const inventoryConfig = inventorySource?.config || {};

  // Extract config values from inventory dataSource config
  const rowType = inventoryConfig.rowType || 'hosts';
  const categoryHandling = inventoryConfig.categoryHandling || 'column';
  const scanBasis = inventoryConfig.scanBasis || 'latest';
  const pivotColumnLimit = inventoryConfig.pivotColumnLimit || 20;

  // Memoize selectedCategories to avoid triggering useCallback re-creation
  const selectedCategories = useMemo(
    () => inventoryConfig.selectedCategories || [],
    [inventoryConfig.selectedCategories],
  );

  // Calculate tableLayout based on rowType
  const tableLayout = inventoryConfig.tableLayout || (rowType === 'hosts' ? 'long_form' : 'aggregated');

  // Helper to update inventory config
  const handleConfigChange = useCallback(
    (field, value) => {
      if (onDataSourceConfigChange) {
        onDataSourceConfigChange(field, value, DATA_SOURCE_TYPES.INVENTORY);
      }
    },
    [onDataSourceConfigChange],
  );

  // Get available layouts based on row type
  const availableLayouts = rowType === 'hosts' ? INVENTORY_LAYOUTS_HOSTS : INVENTORY_LAYOUTS_ITEMS;

  // Load available inventory categories
  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const response = await fetchInventoryCategories();
      if (response.status === 'success' && response.data) {
        setAvailableCategories(response.data);
      } else {
        setAvailableCategories([]);
      }
    } catch {
      setAvailableCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Handle row type change - reset layout to default for new row type
  const handleRowTypeChange = useCallback((newRowType) => {
    handleConfigChange('rowType', newRowType);
    // Reset layout to default for the new row type
    const defaultLayout = newRowType === 'hosts' ? 'long_form' : 'aggregated';
    handleConfigChange('tableLayout', defaultLayout);
  }, [handleConfigChange]);

  // Handle category selection for "specific categories" option
  const handleCategoryToggle = useCallback((category) => {
    const current = selectedCategories || [];
    const updated = current.includes(category)
      ? current.filter((c) => c !== category)
      : [...current, category];
    handleConfigChange('selectedCategories', updated);
  }, [selectedCategories, handleConfigChange]);

  // Collect errors
  const errorMessages = [];
  if (validationErrors.inventoryRowType) errorMessages.push(validationErrors.inventoryRowType);
  if (validationErrors.inventoryTableLayout) errorMessages.push(validationErrors.inventoryTableLayout);
  if (validationErrors.inventorySelectedCategories) errorMessages.push(validationErrors.inventorySelectedCategories);

  return (
    <div className={styles.container}>
      <h2 className={wizardStyles.stepTitle}>{t('dashboard.widgetWizard.inventory.configTitle')}</h2>

      <ErrorBox errors={errorMessages} />

      {/* Section 1: Row Type Selection */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('dashboard.widgetWizard.inventory.rowTypeTitle')}
          <span className={styles.required}>*</span>
        </h3>
        <p className={styles.sectionDescription}>
          {t('dashboard.widgetWizard.inventory.rowTypeDescription')}
        </p>
        <div className={styles.radioGroup} role="radiogroup" aria-label={t('dashboard.widgetWizard.inventory.rowTypeTitle')}>
          {INVENTORY_ROW_TYPES.map((option) => (
            <label key={option.value} className={styles.radioLabel} htmlFor={`rowType-${option.value}`}>
              <input
                id={`rowType-${option.value}`}
                type="radio"
                name="inventoryRowType"
                value={option.value}
                checked={rowType === option.value}
                onChange={() => handleRowTypeChange(option.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioText}>{t(option.label)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Section 2: Category Handling */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('dashboard.widgetWizard.inventory.categoryHandlingTitle')}
        </h3>
        <p className={styles.sectionDescription}>
          {t('dashboard.widgetWizard.inventory.categoryHandlingDescription')}
        </p>
        <div className={styles.radioGroup} role="radiogroup" aria-label={t('dashboard.widgetWizard.inventory.categoryHandlingTitle')}>
          {INVENTORY_CATEGORY_HANDLING.map((option) => (
            <label key={option.value} className={styles.radioLabel} htmlFor={`categoryHandling-${option.value}`}>
              <input
                id={`categoryHandling-${option.value}`}
                type="radio"
                name="inventoryCategoryHandling"
                value={option.value}
                checked={categoryHandling === option.value}
                onChange={() => handleConfigChange('categoryHandling', option.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioText}>{t(option.label)}</span>
            </label>
          ))}
        </div>

        {/* Category Picker - shown when "specific categories" is selected */}
        {categoryHandling === 'specific' && (
          <div className={styles.categoryPicker}>
            <p className={styles.pickerLabel}>
              {t('dashboard.widgetWizard.inventory.selectCategories')}
            </p>
            {isLoadingCategories && (
              <div className={styles.loadingState}>
                {t('dashboard.widgetWizard.inventory.loadingCategories')}
              </div>
            )}
            {!isLoadingCategories && availableCategories.length > 0 && (
              <div className={styles.categoryList}>
                {availableCategories.map((category) => (
                  <label key={category} className={styles.checkboxLabel} htmlFor={`category-${category}`}>
                    <input
                      id={`category-${category}`}
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => handleCategoryToggle(category)}
                      className={styles.checkboxInput}
                    />
                    <span className={styles.checkboxText}>{category}</span>
                  </label>
                ))}
              </div>
            )}
            {!isLoadingCategories && availableCategories.length === 0 && (
              <div className={styles.emptyState}>
                {t('dashboard.widgetWizard.inventory.noCategoriesAvailable')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 3: Table Layout */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('dashboard.widgetWizard.inventory.tableLayoutTitle')}
          <span className={styles.required}>*</span>
        </h3>
        <p className={styles.sectionDescription}>
          {rowType === 'hosts'
            ? t('dashboard.widgetWizard.inventory.tableLayoutDescriptionHosts')
            : t('dashboard.widgetWizard.inventory.tableLayoutDescriptionItems')}
        </p>
        <div className={styles.layoutOptions}>
          {availableLayouts.map((option) => (
            <div
              key={option.value}
              className={`${styles.layoutCard} ${tableLayout === option.value ? styles.layoutCardSelected : ''}`}
              onClick={() => handleConfigChange('tableLayout', option.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleConfigChange('tableLayout', option.value);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={styles.layoutIcon}>
                {getLayoutIcon(option.value)}
              </div>
              <div className={styles.layoutInfo}>
                <span className={styles.layoutName}>{t(option.label)}</span>
                <span className={styles.layoutDescription}>
                  {t(`dashboard.widgetWizard.inventory.layoutDescription.${option.value}`)}
                </span>
              </div>
              {tableLayout === option.value && (
                <div className={styles.selectedIndicator}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pivot Column Limit - shown when pivot layout is selected */}
        {tableLayout === 'pivot' && (
          <div className={styles.pivotOptions}>
            <label className={styles.inputLabel} htmlFor="pivotColumnLimit">
              {t('dashboard.widgetWizard.inventory.pivotColumnLimit')}
            </label>
            <input
              id="pivotColumnLimit"
              type="number"
              min="1"
              max="50"
              value={pivotColumnLimit}
              onChange={(e) => handleConfigChange('pivotColumnLimit', parseInt(e.target.value, 10) || 20)}
              className={styles.numberInput}
            />
            <p className={styles.inputHelp}>
              {t('dashboard.widgetWizard.inventory.pivotColumnLimitHelp')}
            </p>
          </div>
        )}
      </div>

      {/* Section 4: Scan Basis */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('dashboard.widgetWizard.inventory.scanBasisTitle')}
        </h3>
        <p className={styles.sectionDescription}>
          {t('dashboard.widgetWizard.inventory.scanBasisDescription')}
        </p>
        <div className={styles.radioGroup} role="radiogroup" aria-label={t('dashboard.widgetWizard.inventory.scanBasisTitle')}>
          {INVENTORY_SCAN_BASIS.map((option) => (
            <label key={option.value} className={styles.radioLabel} htmlFor={`scanBasis-${option.value}`}>
              <input
                id={`scanBasis-${option.value}`}
                type="radio"
                name="inventoryScanBasis"
                value={option.value}
                checked={scanBasis === option.value}
                onChange={() => handleConfigChange('scanBasis', option.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioText}>{t(option.label)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Preview Summary */}
      <div className={styles.summarySection}>
        <h3 className={styles.sectionTitle}>
          {t('dashboard.widgetWizard.inventory.summaryTitle')}
        </h3>
        <div className={styles.summaryContent}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('dashboard.widgetWizard.inventory.rowTypeTitle')}:</span>
            <span className={styles.summaryValue}>
              {t(INVENTORY_ROW_TYPES.find((r) => r.value === rowType)?.label || '')}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('dashboard.widgetWizard.inventory.tableLayoutTitle')}:</span>
            <span className={styles.summaryValue}>
              {t(availableLayouts.find((l) => l.value === tableLayout)?.label || '')}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('dashboard.widgetWizard.inventory.scanBasisTitle')}:</span>
            <span className={styles.summaryValue}>
              {t(INVENTORY_SCAN_BASIS.find((s) => s.value === scanBasis)?.label || '')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

Step3InventoryConfig.propTypes = {
  widgetData: PropTypes.shape({
    type: PropTypes.string,
    config: PropTypes.shape({
      dataSources: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string,
          config: PropTypes.shape({
            rowType: PropTypes.string,
            categoryHandling: PropTypes.string,
            tableLayout: PropTypes.string,
            scanBasis: PropTypes.string,
            selectedCategories: PropTypes.arrayOf(PropTypes.string),
            pivotColumnLimit: PropTypes.number,
          }),
        }),
      ),
    }),
  }).isRequired,
  onDataSourceConfigChange: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    inventoryRowType: PropTypes.string,
    inventoryTableLayout: PropTypes.string,
    inventorySelectedCategories: PropTypes.string,
  }),
};

Step3InventoryConfig.defaultProps = {
  validationErrors: {},
};

export default Step3InventoryConfig;

